export const getLocalDayKey = (dateValue) => {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  const day = String(dateValue.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatDayLabelFromDayKey = (dayKey) => {
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  }).format(new Date(`${dayKey}T00:00:00`));
};

export const formatDateTimeLocal = (dateValue) => {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  const day = String(dateValue.getDate()).padStart(2, "0");
  const hours = String(dateValue.getHours()).padStart(2, "0");
  const minutes = String(dateValue.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const extractDatePart = (value) => {
  if (!value) return "";
  return String(value).split("T")[0] || "";
};

export const extractTimePart = (value) => {
  if (!value) return "09:00";
  const timePart = String(value).split("T")[1] || "";
  const hhmm = timePart.slice(0, 5);
  return /^\d{2}:\d{2}$/.test(hhmm) ? hhmm : "09:00";
};

export const getDayKeysInRange = (firstDateTime, lastDateTime) => {
  if (!firstDateTime || !lastDateTime) return [];
  const startDate = extractDatePart(firstDateTime);
  const endDate = extractDatePart(lastDateTime);
  if (!startDate || !endDate) return [];
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];

  const keys = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    keys.push(getLocalDayKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
};

export const toMidnightDate = (dateStringOrDateTime) => {
  const datePart = extractDatePart(dateStringOrDateTime);
  const [year, month, day] = String(datePart).split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

export const DURATION_UNIT_MINUTES = {
  minutes: 1,
  hours: 60,
  days: 24 * 60,
};

export const getSlotCapacity = (response) => {
  const capacity = Number(response?.capacity);
  if (!Number.isFinite(capacity) || capacity < 1) return 1;
  return Math.floor(capacity);
};

export const durationValueToMinutes = (value, unit) => {
  const amount = Number(value);
  const factor = DURATION_UNIT_MINUTES[unit] || 1;
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return amount * factor;
};

export const resolveDurationFields = (surveyData, durationMinutes) => {
  const minutes = Number(durationMinutes);
  const storedUnit = surveyData?.interventionDurationUnit;
  const unit = Object.prototype.hasOwnProperty.call(DURATION_UNIT_MINUTES, storedUnit)
    ? storedUnit
    : "minutes";
  const factor = DURATION_UNIT_MINUTES[unit];
  const value = minutes / factor;
  if (Number.isFinite(minutes) && minutes > 0 && Number.isInteger(value) && value > 0) {
    return { durationValue: value, durationUnit: unit, durationMinutes: minutes };
  }
  return {
    durationValue: Number.isFinite(minutes) && minutes > 0 ? minutes : 30,
    durationUnit: "minutes",
    durationMinutes: Number.isFinite(minutes) && minutes > 0 ? minutes : 30,
  };
};

/** La durée tient dans la journée, y compris si elle se termine à minuit. */
export const slotFitsInDay = (dayStartTime, durationInMinutes) => {
  if (!dayStartTime || !durationInMinutes || durationInMinutes <= 0) return false;
  const [hours, minutes] = String(dayStartTime).split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;
  return hours * 60 + minutes + durationInMinutes <= 24 * 60;
};

const formatFrenchDate = (dateValue) =>
  new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dateValue);

const formatFrenchTime = (dateValue) =>
  new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(dateValue);

export const isAllDayRange = (startTs, endTs) => {
  if (startTs == null || endTs == null || endTs <= startTs) return false;
  if (endTs - startTs < 20 * 60 * 60 * 1000) return false;
  const start = new Date(startTs);
  const end = new Date(endTs);
  return (
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    end.getHours() === 0 &&
    end.getMinutes() === 0
  );
};

export const timestampOnDay = (dayKey, timeValue) => {
  const [hours, minutes] = String(timeValue || "").split(":").map(Number);
  if (!dayKey || Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  const dateValue = new Date(`${dayKey}T00:00:00`);
  if (Number.isNaN(dateValue.getTime())) return null;
  dateValue.setHours(hours, minutes, 0, 0);
  return dateValue.getTime();
};

export const getSlotDays = (response) => {
  if (!Array.isArray(response?.days)) return [];
  return response.days
    .map((day) => ({
      startTs: typeof day?.startTs === "number" ? day.startTs : Number(day?.startTs),
      endTs: typeof day?.endTs === "number" ? day.endTs : Number(day?.endTs),
    }))
    .filter((day) => Number.isFinite(day.startTs) && Number.isFinite(day.endTs) && day.endTs > day.startTs);
};

export const isMultiDaySlot = (response) => getSlotDays(response).length > 1;

export const getDayWindowBlockIdFromDays = (days) =>
  (days || []).map((day) => `${day.startTs}-${day.endTs}`).join("|");

export const getDayWindowBlockId = (response) => {
  const days = getSlotDays(response);
  if (days.length === 0) return null;
  return getDayWindowBlockIdFromDays(days);
};

export const formatMultiDaySlotTitle = (days) => {
  if (!days?.length) return "";
  const first = formatFrenchDate(new Date(days[0].startTs));
  const last = formatFrenchDate(new Date(days[days.length - 1].startTs));
  if (days.length === 1) return first;
  return `${first} → ${last}`;
};

const collectSlotBoundInstants = (response) => {
  const days = getSlotDays(response);
  if (days.length > 0) {
    return {
      starts: days.map((day) => day.startTs),
      ends: days.map((day) => day.endTs),
    };
  }
  const startTs = Number(response?.startTs);
  const endTs = Number(response?.endTs);
  return {
    starts: Number.isFinite(startTs) ? [startTs] : [],
    ends: Number.isFinite(endTs) ? [endTs] : [],
  };
};

const isLocalMidnight = (dateValue) =>
  dateValue.getHours() === 0 &&
  dateValue.getMinutes() === 0 &&
  dateValue.getSeconds() === 0 &&
  dateValue.getMilliseconds() === 0;

/** Minuit local du jour concerné. Une fin à minuit pile compte pour le jour précédent. */
const calendarDayMsFromTimestamp = (timestamp, isEnd) => {
  const dateValue = new Date(timestamp);
  if (Number.isNaN(dateValue.getTime())) return null;
  if (isEnd && isLocalMidnight(dateValue)) {
    dateValue.setDate(dateValue.getDate() - 1);
  }
  dateValue.setHours(0, 0, 0, 0);
  return dateValue.getTime();
};

export const formatSlotDayLines = (days) => {
  return (days || []).map((day) => {
    const start = new Date(day.startTs);
    const dateLabel = new Intl.DateTimeFormat("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(start);
    return `${dateLabel} : ${formatFrenchTime(start)} - ${formatFrenchTime(new Date(day.endTs))}`;
  });
};

export const toStoredCreneauSlot = (slot) => {
  const stored = {
    text: slot.text,
    startTs: slot.startTs,
    endTs: slot.endTs,
    capacity: getSlotCapacity(slot),
  };
  if (slot.nbAnswers != null) stored.nbAnswers = slot.nbAnswers;
  const days = getSlotDays(slot);
  if (days.length > 0) stored.days = days;
  return stored;
};

export function buildDayWindowBlocks(dayKeys, dayWindows, durationDays) {
  const size = Number(durationDays);
  if (!Array.isArray(dayKeys) || !Number.isInteger(size) || size <= 0) return [];

  const slots = [];
  for (let index = 0; index + size <= dayKeys.length; index += size) {
    const keys = dayKeys.slice(index, index + size);
    const days = [];
    let valid = true;
    for (const dayKey of keys) {
      const window = dayWindows?.[dayKey] || {};
      const startTs = timestampOnDay(dayKey, window.start);
      const endTs = timestampOnDay(dayKey, window.end);
      if (startTs == null || endTs == null || endTs <= startTs) {
        valid = false;
        break;
      }
      days.push({ startTs, endTs });
    }
    if (!valid) continue;

    const startTs = days[0].startTs;
    const endTs = days[days.length - 1].endTs;
    const firstDate = formatFrenchDate(new Date(days[0].startTs));
    const text = days.length === 1
      ? `${firstDate} ${formatFrenchTime(new Date(startTs))} - ${formatFrenchTime(new Date(endTs))}`
      : days
        .map((day) => `${formatFrenchDate(new Date(day.startTs))} ${formatFrenchTime(new Date(day.startTs))}-${formatFrenchTime(new Date(day.endTs))}`)
        .join(" → ");

    slots.push({
      id: getDayWindowBlockIdFromDays(days),
      startTs,
      endTs,
      days,
      dayKey: keys[0],
      dayLabel: formatDayLabelFromDayKey(keys[0]),
      timeLabel: days.length === 1
        ? `${formatFrenchTime(new Date(startTs))} - ${formatFrenchTime(new Date(endTs))}`
        : formatMultiDaySlotTitle(days),
      text,
    });
  }

  return slots;
}

export const inferDayWindowsFromResponses = (responses) => {
  const windows = {};
  (responses || []).forEach((response) => {
    getSlotDays(response).forEach((day) => {
      const start = new Date(day.startTs);
      const dayKey = getLocalDayKey(start);
      if (windows[dayKey]) return;
      const end = new Date(day.endTs);
      windows[dayKey] = {
        start: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
        end: `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`,
      };
    });
  });
  return windows;
};

export const formatCreneauRangeLabel = (startTs, endTs) => {
  if (isAllDayRange(startTs, endTs)) {
    const startLabel = formatFrenchDate(new Date(startTs));
    const lastIncludedLabel = formatFrenchDate(new Date(endTs - 1));
    if (startLabel === lastIncludedLabel) return startLabel;
    return `${startLabel} → ${lastIncludedLabel}`;
  }
  return `${formatFrenchDate(new Date(startTs))} ${formatFrenchTime(new Date(startTs))}`;
};

export const slotsOverlap = (startA, endA, startB, endB) => startA < endB && endA > startB;

export const getResponseStartTs = (response) => {
  const startTs = typeof response?.startTs === "number" ? response.startTs : Number(response?.startTs);
  return Number.isNaN(startTs) ? null : startTs;
};

export const sortSurveyResponsesByStartTs = (responses) => {
  return [...(responses || [])].sort((a, b) => {
    const aTs = getResponseStartTs(a);
    const bTs = getResponseStartTs(b);
    if (aTs == null && bTs == null) return 0;
    if (aTs == null) return 1;
    if (bTs == null) return -1;
    return aTs - bTs;
  });
};

export const answerMatchesSlotText = (answerData, slotText) => {
  if (slotText == null) return false;
  const selected = answerData?.answer || [];
  return selected.some((part) => String(part) === String(slotText));
};

/** startTs Firestore de l'answer si le créneau a une réponse acceptée, sinon celui du créneau sondage. */
export const getCreneauSlotSortStartTs = (surveyResponse, acceptedAnswers = []) => {
  const slotText = surveyResponse?.text;
  if (slotText != null) {
    for (const answerData of acceptedAnswers) {
      if (!answerMatchesSlotText(answerData, slotText)) continue;
      const answerStartTs = getResponseStartTs(answerData);
      if (answerStartTs != null) return answerStartTs;
    }
  }
  return getResponseStartTs(surveyResponse);
};

export const sortCreneauResponsesByStartTs = (responses, acceptedAnswers = []) => {
  return [...(responses || [])].sort((a, b) => {
    const aTs = getCreneauSlotSortStartTs(a, acceptedAnswers);
    const bTs = getCreneauSlotSortStartTs(b, acceptedAnswers);
    if (aTs == null && bTs == null) return 0;
    if (aTs == null) return 1;
    if (bTs == null) return -1;
    return aTs - bTs;
  });
};

export const getResponseEndTs = (response) => {
  const endTs = typeof response?.endTs === "number" ? response.endTs : Number(response?.endTs);
  return Number.isNaN(endTs) ? null : endTs;
};

export const slotOverlapsResponses = (startTs, endTs, responses) => {
  return (responses || []).some((response) => {
    const responseStartTs = getResponseStartTs(response);
    const responseEndTs = getResponseEndTs(response);
    if (responseStartTs == null || responseEndTs == null) return false;
    return slotsOverlap(startTs, endTs, responseStartTs, responseEndTs);
  });
};

export const buildSlotTextsWithAcceptedAnswers = (acceptedAnswers) => {
  const slotTexts = new Set();
  (acceptedAnswers || []).forEach((answerData) => {
    (answerData?.answer || []).forEach((slotText) => {
      if (slotText != null) slotTexts.add(String(slotText));
    });
  });
  return slotTexts;
};

export const countAcceptedAnswersForSlotText = (slotText, acceptedAnswers) => {
  if (!slotText) return 0;
  return (acceptedAnswers || []).filter((answerData) =>
    (answerData?.answer || []).includes(slotText)
  ).length;
};

export const isCreneauResponseDeletable = (response, reservedSlotTexts) => {
  if (!response?.text) return true;
  return !reservedSlotTexts?.has(response.text);
};

export const inferDayStartTimesFromResponses = (responses) => {
  const earliestByDay = {};
  (responses || []).forEach((response) => {
    const startTs = getResponseStartTs(response);
    if (startTs == null) return;
    const dayKey = getLocalDayKey(new Date(startTs));
    if (earliestByDay[dayKey] == null || startTs < earliestByDay[dayKey]) {
      earliestByDay[dayKey] = startTs;
    }
  });

  const dayStartTimes = {};
  Object.entries(earliestByDay).forEach(([dayKey, startTs]) => {
    const dateValue = new Date(startTs);
    const hours = String(dateValue.getHours()).padStart(2, "0");
    const minutes = String(dateValue.getMinutes()).padStart(2, "0");
    dayStartTimes[dayKey] = `${hours}:${minutes}`;
  });
  return dayStartTimes;
};

export function buildDaySlots(dayKey, dayStartTime, durationInMinutes) {
  if (!dayKey || !dayStartTime || !durationInMinutes || durationInMinutes <= 0) return [];

  const [hours, minutes] = dayStartTime.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return [];

  const dayStart = new Date(`${dayKey}T00:00:00`);
  const nextMidnight = new Date(dayStart);
  nextMidnight.setDate(nextMidnight.getDate() + 1);
  const firstSlotStart = new Date(dayStart);
  firstSlotStart.setHours(hours, minutes, 0, 0);

  const slots = [];
  let cursor = new Date(firstSlotStart);
  while (cursor < nextMidnight) {
    const slotEnd = new Date(cursor.getTime() + durationInMinutes * 60000);
    if (slotEnd > nextMidnight) {
      break;
    }

    const startTs = cursor.getTime();
    slots.push({
      id: String(startTs),
      startTs,
      endTs: slotEnd.getTime(),
      dayKey,
      dayLabel: formatDayLabelFromDayKey(dayKey),
      timeLabel: formatFrenchTime(new Date(startTs)),
      text: `${formatFrenchDate(new Date(startTs))} ${formatFrenchTime(new Date(startTs))}`,
    });

    cursor = slotEnd;
  }

  return slots;
}

export function buildCalendarDaySlots(firstDate, lastDate, durationDays) {
  const days = Number(durationDays);
  if (!firstDate || !lastDate || !Number.isInteger(days) || days <= 0) return [];

  const start = toMidnightDate(firstDate);
  const last = toMidnightDate(lastDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(last.getTime()) || start > last) return [];

  const rangeEndExclusive = new Date(last);
  rangeEndExclusive.setDate(rangeEndExclusive.getDate() + 1);

  const slots = [];
  let cursor = new Date(start);
  while (cursor < rangeEndExclusive) {
    const slotEnd = new Date(cursor);
    slotEnd.setDate(slotEnd.getDate() + days);
    if (slotEnd > rangeEndExclusive) break;

    const startTs = cursor.getTime();
    const endTs = slotEnd.getTime();
    const lastIncluded = new Date(endTs - 1);
    slots.push({
      id: String(startTs),
      startTs,
      endTs,
      dayKey: getLocalDayKey(cursor),
      dayLabel: formatDayLabelFromDayKey(getLocalDayKey(cursor)),
      timeLabel: days === 1
        ? "Journée"
        : `au ${new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short" }).format(lastIncluded)}`,
      text: formatCreneauRangeLabel(startTs, endTs),
    });
    cursor = slotEnd;
  }

  return slots;
}

export const normalizeDateTimeToStep = (value, stepMinutes = 5) => {
  if (!value) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const normalized = new Date(parsed);
  normalized.setSeconds(0, 0);
  const minutes = normalized.getMinutes();
  const roundedMinutes = Math.floor(minutes / stepMinutes) * stepMinutes;
  normalized.setMinutes(roundedMinutes);
  return formatDateTimeLocal(normalized);
};

export function computeSlotMeta(surveyData) {
  const existingResponses = surveyData.responses || [];
  const existingStartTsSet = new Set();
  existingResponses.forEach((r) => {
    const st = typeof r.startTs === "number" ? r.startTs : Number(r.startTs);
    if (!Number.isNaN(st)) existingStartTsSet.add(st);
  });

  const startDays = [];
  const endDays = [];
  existingResponses.forEach((response) => {
    const { starts, ends } = collectSlotBoundInstants(response);
    starts.forEach((startTs) => {
      const dayMs = calendarDayMsFromTimestamp(startTs, false);
      if (dayMs != null) startDays.push(dayMs);
    });
    ends.forEach((endTs) => {
      const dayMs = calendarDayMsFromTimestamp(endTs, true);
      if (dayMs != null) endDays.push(dayMs);
    });
  });

  const boundFirstMs = startDays.length ? Math.min(...startDays) : Date.now();
  const boundLastMs = endDays.length ? Math.max(...endDays) : boundFirstMs;

  let interventionDuration = surveyData.interventionDuration;
  if (interventionDuration == null || interventionDuration <= 0) {
    const withRange = existingResponses.find(
      (r) => typeof r.startTs === "number" && typeof r.endTs === "number"
    );
    if (withRange) {
      interventionDuration = Math.round((withRange.endTs - withRange.startTs) / 60000);
    }
  }
  if (!interventionDuration || interventionDuration <= 0) {
    interventionDuration = 30;
  }

  const durationFields = resolveDurationFields(surveyData, Number(interventionDuration));

  return {
    existingResponses,
    existingStartTsSet,
    boundFirstMs,
    boundLastMs,
    interventionDuration: durationFields.durationMinutes,
    durationValue: durationFields.durationValue,
    durationUnit: durationFields.durationUnit,
  };
}

export function generateSlots(firstInterventionDate, lastInterventionDate, durationInMinutes) {
  if (!firstInterventionDate || !lastInterventionDate || !durationInMinutes || durationInMinutes <= 0) {
    return [];
  }

  const start = new Date(normalizeDateTimeToStep(firstInterventionDate, 5));
  const lastSlotStart = new Date(normalizeDateTimeToStep(lastInterventionDate, 5));

  if (Number.isNaN(start.getTime()) || Number.isNaN(lastSlotStart.getTime()) || start > lastSlotStart) {
    return [];
  }

  const anchorHours = start.getHours();
  const anchorMinutes = start.getMinutes();
  const slots = [];
  let cursor = new Date(start);

  while (cursor <= lastSlotStart) {
    const slotEnd = new Date(cursor.getTime() + durationInMinutes * 60000);
    const cursorDay = cursor.toDateString();
    const slotEndDay = slotEnd.toDateString();

    if (cursorDay !== slotEndDay) {
      const nextDayStart = new Date(cursor);
      nextDayStart.setDate(nextDayStart.getDate() + 1);
      nextDayStart.setHours(anchorHours, anchorMinutes, 0, 0);
      cursor = nextDayStart;
      continue;
    }

    const startTs = cursor.getTime();
    slots.push({
      id: String(startTs),
      startTs,
      endTs: slotEnd.getTime(),
      dayKey: getLocalDayKey(new Date(startTs)),
      dayLabel: new Intl.DateTimeFormat("fr-FR", {
        weekday: "short",
        day: "2-digit",
        month: "short",
      }).format(new Date(startTs)),
      timeLabel: new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(startTs)),
      text: `${new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(startTs))} ${new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(startTs))}`,
    });

    cursor = slotEnd;
  }

  return slots;
}
